export default function CookiesPage() {
  return (
    <>
      <section className="py-20 sm:py-28 bg-gradient-to-br from-primary-700 via-primary-600 to-primary-700">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-3xl text-center">
            <h1 className="text-4xl font-bold tracking-tight text-white sm:text-5xl lg:text-6xl">
              Cookie Policy
            </h1>
            <p className="mt-6 text-lg leading-8 text-primary-100 sm:text-xl">
              Last updated: January 16, 2025
            </p>
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-20 bg-white">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <div className="space-y-8 text-navy-600">
            <div>
              <h2 className="text-2xl font-bold text-primary-700 mb-4">What Are Cookies</h2>
              <p className="mb-4 leading-relaxed">
                Cookies are small text files that are stored on your computer or mobile device when you
                visit a website. They are widely used to make websites work more efficiently, provide
                information to site owners, and improve user experience.
              </p>
              <p className="leading-relaxed">
                This Cookie Policy explains how Clarantis, Inc. ("Clarantis," "we," "us," or "our") uses
                cookies and similar technologies on our website and platform (collectively, the "Services").
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-primary-700 mb-4">How We Use Cookies</h2>
              <p className="mb-6 leading-relaxed">We use cookies for the following purposes:</p>

              <h3 className="text-xl font-semibold text-primary-600 mb-3">Essential Cookies</h3>
              <p className="mb-4 leading-relaxed">
                These cookies are necessary for the Services to function properly. They enable core
                functionality such as security, authentication, and session management. You cannot
                opt out of these cookies as the Services would not work without them.
              </p>
              <div className="overflow-x-auto mb-6">
                <table className="min-w-full border border-navy-200 rounded-lg overflow-hidden">
                  <thead className="bg-navy-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-primary-700 border-b border-navy-200">Cookie Name</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-primary-700 border-b border-navy-200">Purpose</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-primary-700 border-b border-navy-200">Duration</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-navy-100">
                    <tr>
                      <td className="px-4 py-3 text-sm"><code className="bg-navy-100 px-1.5 py-0.5 rounded text-primary-600">session_token</code></td>
                      <td className="px-4 py-3 text-sm">Maintains your authenticated session</td>
                      <td className="px-4 py-3 text-sm">24 hours</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm"><code className="bg-navy-100 px-1.5 py-0.5 rounded text-primary-600">csrf_token</code></td>
                      <td className="px-4 py-3 text-sm">Protects against cross-site request forgery attacks</td>
                      <td className="px-4 py-3 text-sm">Session</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm"><code className="bg-navy-100 px-1.5 py-0.5 rounded text-primary-600">tenant_id</code></td>
                      <td className="px-4 py-3 text-sm">Identifies your organization for multi-tenant access</td>
                      <td className="px-4 py-3 text-sm">30 days</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h3 className="text-xl font-semibold text-primary-600 mb-3">Functional Cookies</h3>
              <p className="mb-4 leading-relaxed">
                These cookies enable enhanced functionality and personalization, such as remembering
                your preferences and settings.
              </p>
              <div className="overflow-x-auto mb-6">
                <table className="min-w-full border border-navy-200 rounded-lg overflow-hidden">
                  <thead className="bg-navy-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-primary-700 border-b border-navy-200">Cookie Name</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-primary-700 border-b border-navy-200">Purpose</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-primary-700 border-b border-navy-200">Duration</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-navy-100">
                    <tr>
                      <td className="px-4 py-3 text-sm"><code className="bg-navy-100 px-1.5 py-0.5 rounded text-primary-600">user_preferences</code></td>
                      <td className="px-4 py-3 text-sm">Stores your display and notification preferences</td>
                      <td className="px-4 py-3 text-sm">1 year</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm"><code className="bg-navy-100 px-1.5 py-0.5 rounded text-primary-600">sidebar_state</code></td>
                      <td className="px-4 py-3 text-sm">Remembers sidebar collapsed/expanded state</td>
                      <td className="px-4 py-3 text-sm">1 year</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm"><code className="bg-navy-100 px-1.5 py-0.5 rounded text-primary-600">theme</code></td>
                      <td className="px-4 py-3 text-sm">Stores your light/dark mode preference</td>
                      <td className="px-4 py-3 text-sm">1 year</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h3 className="text-xl font-semibold text-primary-600 mb-3">Analytics Cookies</h3>
              <p className="mb-4 leading-relaxed">
                These cookies help us understand how visitors interact with our Services by collecting
                and reporting information anonymously. This helps us improve our Services.
              </p>
              <div className="overflow-x-auto">
                <table className="min-w-full border border-navy-200 rounded-lg overflow-hidden">
                  <thead className="bg-navy-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-primary-700 border-b border-navy-200">Cookie Name</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-primary-700 border-b border-navy-200">Purpose</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-primary-700 border-b border-navy-200">Duration</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-navy-100">
                    <tr>
                      <td className="px-4 py-3 text-sm"><code className="bg-navy-100 px-1.5 py-0.5 rounded text-primary-600">_ga</code></td>
                      <td className="px-4 py-3 text-sm">Google Analytics - distinguishes unique users</td>
                      <td className="px-4 py-3 text-sm">2 years</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm"><code className="bg-navy-100 px-1.5 py-0.5 rounded text-primary-600">_gid</code></td>
                      <td className="px-4 py-3 text-sm">Google Analytics - distinguishes users</td>
                      <td className="px-4 py-3 text-sm">24 hours</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm"><code className="bg-navy-100 px-1.5 py-0.5 rounded text-primary-600">_gat</code></td>
                      <td className="px-4 py-3 text-sm">Google Analytics - throttles request rate</td>
                      <td className="px-4 py-3 text-sm">1 minute</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-primary-700 mb-4">Third-Party Cookies</h2>
              <p className="mb-4 leading-relaxed">
                Some cookies are placed by third-party services that appear on our pages. We do not
                control these cookies. Third-party cookies may be set by:
              </p>
              <ul className="list-disc pl-6 space-y-2 mb-4">
                <li><strong>Cloudflare:</strong> For security and performance optimization</li>
                <li><strong>Intercom:</strong> For customer support chat functionality</li>
                <li><strong>Stripe:</strong> For secure payment processing</li>
              </ul>
              <p className="leading-relaxed">
                Please refer to these third parties' privacy policies for more information about their
                cookie practices.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-primary-700 mb-4">Managing Cookies</h2>

              <h3 className="text-xl font-semibold text-primary-600 mb-3">Browser Controls</h3>
              <p className="mb-3 leading-relaxed">
                Most web browsers allow you to manage cookies through their settings. You can:
              </p>
              <ul className="list-disc pl-6 space-y-2 mb-4">
                <li>View what cookies are stored and delete them individually</li>
                <li>Block third-party cookies</li>
                <li>Block all cookies from specific sites</li>
                <li>Block all cookies from being set</li>
                <li>Delete all cookies when you close your browser</li>
              </ul>
              <p className="mb-6 leading-relaxed">
                Please note that blocking or deleting cookies may impact the functionality of our Services.
              </p>

              <h3 className="text-xl font-semibold text-primary-600 mb-3">Browser-Specific Instructions</h3>
              <ul className="list-disc pl-6 space-y-2 mb-6">
                <li>
                  <a href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:text-primary-700 underline">
                    Google Chrome
                  </a>
                </li>
                <li>
                  <a href="https://support.mozilla.org/en-US/kb/cookies-information-websites-store-on-your-computer" target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:text-primary-700 underline">
                    Mozilla Firefox
                  </a>
                </li>
                <li>
                  <a href="https://support.apple.com/guide/safari/manage-cookies-sfri11471/mac" target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:text-primary-700 underline">
                    Safari
                  </a>
                </li>
                <li>
                  <a href="https://support.microsoft.com/en-us/microsoft-edge/delete-cookies-in-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09" target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:text-primary-700 underline">
                    Microsoft Edge
                  </a>
                </li>
              </ul>

              <h3 className="text-xl font-semibold text-primary-600 mb-3">Opt-Out of Analytics</h3>
              <p className="leading-relaxed">
                To opt out of Google Analytics across all websites, you can install the{' '}
                <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:text-primary-700 underline">
                  Google Analytics Opt-out Browser Add-on
                </a>.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-primary-700 mb-4">Do Not Track</h2>
              <p className="leading-relaxed">
                Some browsers have a "Do Not Track" feature that signals to websites that you do not
                want your online activity tracked. Our Services currently do not respond to Do Not Track
                signals, but you can manage cookies as described above.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-primary-700 mb-4">Updates to This Policy</h2>
              <p className="leading-relaxed">
                We may update this Cookie Policy from time to time to reflect changes in our practices
                or for operational, legal, or regulatory reasons. We will post any changes on this page
                with an updated "Last updated" date.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-primary-700 mb-4">Contact Us</h2>
              <p className="mb-3 leading-relaxed">
                If you have questions about our use of cookies, please contact us:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Email: <a href="mailto:privacy@clarantis.com" className="text-primary-600 hover:text-primary-700 underline">privacy@clarantis.com</a></li>
                <li>Address: Clarantis, Inc., Privacy Team</li>
              </ul>
            </div>
          </div>
        </div>
      </section>
    </>
  )
}
